/**
 * Domain types of the core: bookings and counts (immutable, see docs/04-domaenenmodell.md),
 * master data (articles, storages, locations) as entities with a clock per field.
 *
 * Units: quantities are integers in the article's base unit (ml for every drink since F18, else g
 * or Stk.); a receipt stores the amount in whole cents for its whole quantity, because cents per ml
 * do not come out even (12,50 € a bottle are 1,666… ct per ml). Internally the ledger divides it
 * into a price per base unit with fractions of a cent. Times are milliseconds since 1970 (UTC).
 */

export type Id = string;
export type Millis = number;

/** How exactly an article is tracked. Single items (Einzelstück) follow after Phase 0. */
export type Tracking = "quantity" | "lot";

/** How an inventory counts lot articles (F8): per article and location, or per lot. */
export type CountMode = "article" | "lot";

/** Optional data of a lot, given with the receipt that creates it. */
export interface LotInfo {
  bestBefore?: string;
  /** Day it was frozen (C1): set by „Einfrieren“, with the new best-before. */
  frozen?: string;
  supplier?: string;
  batch?: string;
}

/** What the person typed, kept so a booking shows as entered ("5 Kisten"). */
export interface Entered {
  qty: number;
  unit: string;
}

interface BookingBase {
  /** Unique, created on the device (UUIDv7). */
  id: Id;
  /** When it happened (fachlicher Zeitpunkt). */
  at: Millis;
  /** When it was entered on the device. */
  recordedAt: Millis;
  person?: string;
  device?: string;
  note?: string;
  entered?: Entered;
}

/** Zugang: + qty at a location. Creates a new lot for lot articles unless `lot` names an existing one. */
export interface Receipt extends BookingBase {
  kind: "in";
  article: Id;
  lager: Id;
  location: Id;
  qty: number;
  reason: Id;
  /** What the whole quantity cost, in cents (docs/17-getraenke.md, Preise). */
  amount?: number;
  lot?: Id;
  lotInfo?: LotInfo;
}

/** Abgang: − qty at a location. Without `lot`, the oldest lots go first. */
export interface Issue extends BookingBase {
  kind: "out";
  article: Id;
  lager: Id;
  location: Id;
  qty: number;
  reason: Id;
  lot?: Id;
}

/**
 * Umlagerung: − at the source, + at the target; lots keep their identity. With `lotInfo`
 * („Einfrieren“, C1) each part arrives as a new lot with these details (a new best-before), its
 * price, receipt time and supplier kept.
 */
export interface Transfer extends BookingBase {
  kind: "move";
  article: Id;
  fromLager: Id;
  from: Id;
  toLager: Id;
  to: Id;
  qty: number;
  lot?: Id;
  lotInfo?: LotInfo;
}

/** Umwandlung: − article A, + article B at the same location; the value goes along. */
export interface Conversion extends BookingBase {
  kind: "convert";
  lager: Id;
  location: Id;
  fromArticle: Id;
  fromQty: number;
  toArticle: Id;
  toQty: number;
}

/** Zählung: sets the stock to the counted quantity. The difference is always derived, never stored. */
export interface Count extends BookingBase {
  kind: "count";
  article: Id;
  lager: Id;
  location: Id;
  qty: number;
  /** Count of a single lot (count mode "lot"). */
  lot?: Id;
  inventory?: Id;
}

/** Storno: the cancelled booking counts as never booked; both stay in the journal. */
export interface Cancel extends BookingBase {
  kind: "cancel";
  cancels: Id;
  /** Storages of the cancelled booking, so partial sync delivers the cancel with it. */
  lagers: Id[];
}

export type Booking = Receipt | Issue | Transfer | Conversion | Count | Cancel;

/** The storages a booking touches (for partial sync and scope). */
export function lagersOf(booking: Booking): Id[] {
  switch (booking.kind) {
    case "move":
      return booking.fromLager === booking.toLager ? [booking.fromLager] : [booking.fromLager, booking.toLager];
    case "cancel":
      return booking.lagers;
    default:
      return [booking.lager];
  }
}

/** Canonical order of the journal: by time, then by id. Every device computes in this order. */
export function compareBookings(a: Booking, b: Booking): number {
  return a.at - b.at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}
